import { PDFPage, PDFFont, rgb } from "pdf-lib";

export const transitionTableGenerator = (
  page: PDFPage,
  tableX: number,
  tableY: number,
  columnWidths: number[],
  rowHeight: number,
  headers: string[],
  data: string[][],
  font: PDFFont,
  boldFont: PDFFont,
  fontColor: any,
  leftPadding: number
) => {
  const headerBackground = rgb(47 / 255, 59 / 255, 76 / 255);
  const rowBackground = rgb(0.9, 0.9, 0.9);
  const adjustedTableX = tableX + leftPadding;
  let currentY = tableY;

  const wrapText = (
    text: string,
    maxWidth: number,
    font: PDFFont,
    fontSize: number
  ) => {
    const manualLines = text.split("\n");
    let wrappedLines: string[] = [];

    manualLines.forEach((line) => {
      const words = line.split(" ");
      let currentLine = "";

      words.forEach((word) => {
        const testLine = currentLine.length ? `${currentLine} ${word}` : word;
        const textWidth = font.widthOfTextAtSize(testLine, fontSize);

        if (textWidth > maxWidth) {
          if (currentLine) wrappedLines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });

      if (currentLine) wrappedLines.push(currentLine);
    });

    return wrappedLines;
  };

  // Determine max header height based on the wrapped lines
  let headerLineCounts = headers.map(
    (header, index) =>
      wrapText(header, columnWidths[index] - 10, boldFont, 9).length
  );
  let maxHeaderLines = Math.max(...headerLineCounts);
  let headerHeight = rowHeight * maxHeaderLines + 2; // Adjusted height for font size 9

  // Draw header background
  page.drawRectangle({
    x: adjustedTableX,
    y: tableY - headerHeight + rowHeight - 6.5, // Fix bottom margin by adjusting Y
    width: columnWidths.reduce((a, b) => a + b, 0),
    height: headerHeight,
    color: headerBackground,
  });

  // Draw headers with line wrapping (Centered properly)
  let currentX = adjustedTableX;
  headers.forEach((header, index) => {
    const wrappedLines = wrapText(
      header,
      columnWidths[index] - 10,
      boldFont,
      9
    );
    let lineSpacing = 10;
    let totalTextHeight = wrappedLines.length * lineSpacing;
    let startY = tableY - (headerHeight - totalTextHeight) / 2 + 5; // Ensures equal top/bottom margin

    wrappedLines.forEach((line, lineIndex) => {
      const textWidth = boldFont.widthOfTextAtSize(line, 9);
      const centeredX = currentX + columnWidths[index] / 2 - textWidth / 2;

      page.drawText(line, {
        x: centeredX,
        y: startY - lineIndex * lineSpacing,
        size: 9,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
    });

    currentX += columnWidths[index];
  });

  // Adjust starting Y position for data rows (accounting for the new header height)
  currentY = tableY - headerHeight; // Adding bottom margin

  let isEvenRow = false;

  // Draw Data Rows
  data.forEach((row) => {
    if (isEvenRow) {
      page.drawRectangle({
        x: adjustedTableX,
        y: currentY - 5,
        width: columnWidths.reduce((a, b) => a + b, 0),
        height: rowHeight * 0.75, // Adjusted row height
        color: rowBackground,
      });
    }

    let textX = adjustedTableX;
    row.forEach((cell, index) => {
      const textWidth = font.widthOfTextAtSize(cell, 9);
      const centeredX = textX + columnWidths[index] / 2 - textWidth / 2;

      page.drawText(cell, {
        x: centeredX,
        y: currentY,
        size: 9,
        font: font,
        color: fontColor,
      });

      textX += columnWidths[index];
    });

    // page.drawLine({
    //   start: { x: adjustedTableX, y: currentY - 5 },
    //   end: {
    //     x: adjustedTableX + columnWidths.reduce((a, b) => a + b, 0),
    //     y: currentY - 5,
    //   },
    //   thickness: 0.5,
    //   color: fontColor,
    // });

    currentY -= rowHeight * 0.75;
    isEvenRow = !isEvenRow;
  });

  return currentY;
};

export const overviewTableGenerator = (
  page: PDFPage,
  tableX: number,
  tableY: number,
  columnWidths: number[],
  rowHeight: number,
  data: { label: string; value: string }[],
  font: PDFFont,
  boldFont: PDFFont,
  fontColor: any
) => {
  const rowBackground = rgb(0.9, 0.9, 0.9);
  let currentY = tableY;
  let isEvenRow = false;

  data.forEach(({ label, value }) => {
    if (isEvenRow) {
      page.drawRectangle({
        x: tableX,
        y: currentY - 5,
        width: columnWidths.reduce((a, b) => a + b, 0),
        height: rowHeight,
        color: rowBackground,
      });
    }

    page.drawText(label, {
      x: tableX + 40,
      y: currentY,
      size: 11,
      font: boldFont,
      color: fontColor,
    });

    page.drawText(value, {
      x: tableX + columnWidths[0] + 50,
      y: currentY,
      size: 11,
      font: font,
      color: fontColor,
    });

    currentY -= rowHeight;
    isEvenRow = !isEvenRow;
  });

  return currentY;
};
